// Theme definitions with simplified color system:
// - mainBackground: Main content area background
// - secondaryBackground: Sidebar background
// - textColor: Primary text color
// - secondaryTextColor: Secondary text color (lighter than primary, for placeholders/subtitles)
// - primaryActionColor: Primary accent/action color (buttons, links, etc.)
// - secondaryActionColor: Secondary accent/action color (complementary to primary)
// - buttonTextColor: Text color for buttons
// - shadowColor: Border/shadow color, slightly more prominent than secondaryBackground

export const themes = {
  dark: {
    name: 'Midnight Commander',
    description: 'Easy on the eyes for late nights',
    mainBackground: '#1F2937', // gray-800
    secondaryBackground: '#111827', // gray-900
    shadowColor: '#374151', // gray-700 - slightly lighter than secondary for borders
    textColor: '#F9FAFB', // gray-50
    secondaryTextColor: '#9CA3AF', // gray-400 - lighter than primary text
    primaryActionColor: '#22D3EE', // cyan-400 (teal)
    secondaryActionColor: '#34D399', // emerald-400 — warm green that sits naturally next to teal
    buttonTextColor: '#000000', // black - high contrast with both light cyan and purple
  },
  moonlight: {
    name: 'Moonlight',
    description: 'Deep space with an electric lunar glow',
    mainBackground: '#12122a',   // slightly lighter than pure bg for content surfaces
    secondaryBackground: '#070711', // deep space — the landing page bg
    shadowColor: '#1a1a2e',      // border/divider — just enough to separate surfaces
    textColor: '#f0f0ff',        // slightly blue-tinted white — warmer than pure #fff
    secondaryTextColor: '#7070a0', // blue-muted, not grey — keeps palette coherent
    primaryActionColor: '#c4ff4d', // acid lime — the MoonScroll signature accent
    secondaryActionColor: '#9b7ff4', // cosmic violet — complement to acid lime on the color wheel
    buttonTextColor: '#070711',  // deep bg as button text — maximum contrast on lime
  },
  monokai: {
    name: 'Darkroom',
    description: 'Late night, low light, high contrast',
    mainBackground: '#1e1624', // dark eggplant — complements the pink primary
    secondaryBackground: '#15101a', // deeper purple-black
    shadowColor: '#2a2030', // slightly lighter for borders
    textColor: '#F8F8F2',
    secondaryTextColor: '#9CA3AF', // gray-400 - lighter than primary text
    primaryActionColor: '#F92672', // pink/magenta — now the lead color
    secondaryActionColor: '#66D9EF', // cyan — contrasting complement
    buttonTextColor: '#FFFFFF', // white — high contrast on both pink and cyan
  },
  ember: {
    name: 'Ember',
    description: 'Sharp and warm, built for daylight',
    mainBackground: '#FFFFFF',
    secondaryBackground: '#F9FAFB', // gray-50
    shadowColor: '#E5E7EB', // gray-200 - slightly darker than secondary for borders
    textColor: '#111827', // gray-900
    secondaryTextColor: '#6B7280', // gray-500 - lighter than primary text
    primaryActionColor: '#ff5705', // custom orange
    secondaryActionColor: '#06B6D4', // cyan-500 (teal)
    buttonTextColor: '#FFFFFF', // white - high contrast with both orange and teal
  },
  light: {
    name: 'Daybreak',
    description: 'Fresh and clean morning vibes',
    mainBackground: '#FFFFFF',
    secondaryBackground: '#F9FAFB', // gray-50
    shadowColor: '#E5E7EB', // gray-200 - slightly darker than secondary for borders
    textColor: '#111827', // gray-900
    secondaryTextColor: '#6B7280', // gray-500 - lighter than primary text
    primaryActionColor: '#06B6D4', // cyan-500 (teal)
    secondaryActionColor: '#10B981', // emerald-500 (green)
    buttonTextColor: '#FFFFFF', // white - high contrast with both teal and green
  },
  cream: {
    name: 'Cream',
    description: 'Cozy and comfortable for long sessions',
    mainBackground: '#FEF9F3',
    secondaryBackground: '#F5F1EB',
    shadowColor: '#E8E3DB', // slightly darker than secondary for borders
    textColor: '#1F2937',
    secondaryTextColor: '#6B7280', // gray-500 - lighter than primary text
    primaryActionColor: '#D97706', // amber-600 (orange)
    secondaryActionColor: '#DC2626', // red-600 (red)
    buttonTextColor: '#FFFFFF', // white - high contrast with both dark orange and red
  },
};

// Get theme from localStorage or default to 'dark'
export const getStoredTheme = () => {
  try {
    const stored = localStorage.getItem('app-theme');
    return stored && themes[stored] ? stored : 'dark';
  } catch {
    return 'dark';
  }
};

// Save theme to localStorage
export const saveTheme = (themeName) => {
  try {
    localStorage.setItem('app-theme', themeName);
  } catch (error) {
    console.error('Failed to save theme:', error);
  }
};

// Apply theme to document using CSS variables
export const applyTheme = (themeName) => {
  const theme = themes[themeName];
  if (!theme) return;

  const root = document.documentElement;
  root.style.setProperty('--theme-main-bg', theme.mainBackground);
  root.style.setProperty('--theme-secondary-bg', theme.secondaryBackground);
  root.style.setProperty('--theme-shadow', theme.shadowColor);
  root.style.setProperty('--theme-text', theme.textColor);
  root.style.setProperty('--theme-secondary-text', theme.secondaryTextColor);
  root.style.setProperty('--theme-primary', theme.primaryActionColor);
  root.style.setProperty('--theme-secondary', theme.secondaryActionColor);
  root.style.setProperty('--theme-button-text', theme.buttonTextColor);
  root.setAttribute('data-theme', themeName);
};
